"use client";

import { useState, useEffect, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  CreditCardIcon,
  InfoIcon,
  AlertTriangleIcon,
  Building2Icon,
  CalendarIcon,
  GaugeIcon,
  UserIcon,
} from "lucide-react";
import { useNonVwfsLessorsQuery } from "@/lib/queries";
import { Skeleton } from "@/components/ui/skeleton";

interface fundingProps {
  onCanProceedChange?: (canProceed: boolean) => void;
}

/** Draft payload for Mulesoft integration (optional fields per flow). */
interface RetailFundingSnapshot {
  isLeased: boolean;
  type?: string;
  label?: string;
  vwfsType?: string;
  siret?: string;
  businessNumber?: string;
  leasingType?: string;
  orderNumber?: string;
  contractDuration?: string;
  maxKm?: string;
}

interface RetailFundingStepPayload {
  funding: RetailFundingSnapshot;
}

// Simulated VWFS SIRET list
const vwfsSiretList = [
  "44271676500012", // LOUN
  "44271676500020", // LLDFI
  "44271676500038", // LOA
];

export function RetailFundingStep({ onCanProceedChange }: fundingProps) {
  const [isLeased, setIsLeased] = useState<string>("");
  const [fundingType, setFundingType] = useState<string>("");
  const [vwfsType, setVwfsType] = useState<string>("");
  const [otherLeasingType, setOtherLeasingType] = useState<string>("");
  const [businessNumber, setBusinessNumber] = useState<string>("");
  const [lessorSiret, setLessorSiret] = useState<string>("");
  const [orderNumber, setOrderNumber] = useState<string>("");
  const [contractDuration, setContractDuration] = useState<string>("");
  const [maxKm, setMaxKm] = useState<string>("");
  const [siretError, setSiretError] = useState<string>("");
  const [driverFirstName, setDriverFirstName] = useState<string>("");
  const [driverLastName, setDriverLastName] = useState<string>("");

  const nonVwfsLessorsQuery = useNonVwfsLessorsQuery({ sort: { name: "asc" } });
  const nonVwfsLessors = nonVwfsLessorsQuery.data?.nonVwfsLessors ?? [];

  const validForm = useCallback((): boolean => {
    if (!isLeased) return false;

    if (isLeased === "no") {
      return !!fundingType;
    }

    // Leased vehicle
    if (!fundingType) return false;

    if (fundingType === "vwfs") {
      if (!vwfsType || !businessNumber) return false;
    } else if (fundingType === "other") {
      if (!otherLeasingType || !lessorSiret || !orderNumber) return false;
      if (siretError) return false;
    }

    // Duration and max km required for all lease types
    if (!contractDuration || !maxKm) return false;

    return true;
  }, [
    isLeased,
    fundingType,
    vwfsType,
    otherLeasingType,
    businessNumber,
    lessorSiret,
    orderNumber,
    contractDuration,
    maxKm,
    siretError,
  ]);

  useEffect(() => {
    onCanProceedChange?.(validForm());
  }, [
    isLeased,
    fundingType,
    vwfsType,
    otherLeasingType,
    businessNumber,
    lessorSiret,
    orderNumber,
    contractDuration,
    maxKm,
    siretError,
    validForm,
    onCanProceedChange,
  ]);

  const getSiretForVwfsType = (type: string): string => {
    switch (type) {
      case "LOUN":
        return "44271676500012";
      case "LLDFI":
        return "44271676500020";
      case "LOA":
        return "44271676500038";
      default:
        return "";
    }
  };

  const validateSiret = (siret: string): boolean => {
    if (fundingType === "other") {
      if (vwfsSiretList.includes(siret)) {
        setSiretError(
          "Ce SIRET correspond à VWFS. Veuillez sélectionner 'Financement VWFS' dans le type de financement.",
        );
        return false;
      }
      const filteredNonVwfsLessors = nonVwfsLessors
        .filter((lessor) => lessor.isActive)
        .map((lessor) => lessor.siret);
      if (!filteredNonVwfsLessors.includes(lessorSiret)) {
        setSiretError("Ce siret n'est pas valide.");
        return false;
      }
    }
    setSiretError("");
    return true;
  };

  const saveData = () => {
    if (!validForm()) return;

    // TODO: adapt to data to fetch and pass to Mulesoft.
    const data: RetailFundingStepPayload = {
      funding: {
        isLeased: isLeased === "yes",
      },
    };

    if (isLeased === "no") {
      data.funding.type = fundingType;
      data.funding.label =
        fundingType === "credit" ? "Crédit classique" : "Comptant";
    } else {
      if (fundingType === "vwfs") {
        data.funding.type = "vwfs";
        data.funding.vwfsType = vwfsType;
        data.funding.siret = getSiretForVwfsType(vwfsType);
        data.funding.businessNumber = businessNumber;
      } else {
        data.funding.type = "other";
        data.funding.leasingType = otherLeasingType;
        data.funding.siret = lessorSiret;
        data.funding.orderNumber = orderNumber;
      }
      data.funding.contractDuration = contractDuration;
      data.funding.maxKm = maxKm;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CreditCardIcon className="h-5 w-5 text-[#005EA8]" />
            <span>Location & Financement</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Initial required question */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Label className="text-sm font-medium">
                Le véhicule est-il loué ? *
              </Label>
            </div>
            <RadioGroup
              className="flex flex-wrap gap-4"
              value={isLeased}
              onValueChange={(value) => {
                setIsLeased(value);
                setFundingType("");
                setVwfsType("");
                setOtherLeasingType("");
                setBusinessNumber("");
                setLessorSiret("");
                setOrderNumber("");
                setContractDuration("");
                setMaxKm("");
                setSiretError("");
              }}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="leased-yes" />
                <Label
                  htmlFor="leased-yes"
                  className="cursor-pointer font-medium"
                >
                  Oui
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="leased-no" />
                <Label
                  htmlFor="leased-no"
                  className="cursor-pointer font-medium"
                >
                  Non
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Case 1: vehicle not leased */}
          {isLeased === "no" && (
            <div className="space-y-4 rounded-lg bg-blue-50 p-4">
              <Label className="text-base font-semibold">
                Type de financement *
              </Label>
              <RadioGroup
                value={fundingType}
                onValueChange={(value) => {
                  setFundingType(value);
                }}
              >
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="credit" id="credit" />
                    <Label
                      htmlFor="credit"
                      className="cursor-pointer font-medium"
                    >
                      Crédit classique
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="cash" id="cash" />
                    <Label
                      htmlFor="cash"
                      className="cursor-pointer font-medium"
                    >
                      Comptant
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Case 2: leased vehicle */}
          {isLeased === "yes" && (
            <div className="space-y-6 rounded-lg p-4">
              <div className="space-y-4">
                <Label className="text-base font-semibold">
                  Type de financement *
                </Label>
                <RadioGroup
                  value={fundingType}
                  onValueChange={(value) => {
                    setFundingType(value);
                    setVwfsType("");
                    setOtherLeasingType("");
                    setBusinessNumber("");
                    setLessorSiret("");
                    setOrderNumber("");
                    setSiretError("");
                  }}
                >
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="vwfs" id="vwfs" />
                      <Label
                        htmlFor="vwfs"
                        className="cursor-pointer font-medium"
                      >
                        Financement VWFS
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="other" id="other" />
                      <Label
                        htmlFor="other"
                        className="cursor-pointer font-medium"
                      >
                        Financement autre que VWFS
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* VWFS financing */}
              {fundingType === "vwfs" && (
                <div className="space-y-4 rounded-lg border-2 p-4">
                  <Alert>
                    <InfoIcon className="h-4 w-4" />
                    <AlertDescription>
                      Le SIRET VWFS sera automatiquement déduit selon votre
                      choix de type de financement.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <Label>Type de financement VWFS *</Label>
                    <Select
                      value={vwfsType}
                      onValueChange={(value) => {
                        setVwfsType(value);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner le type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LOUN">LOUN</SelectItem>
                        <SelectItem value="LLDFI">LLDFI</SelectItem>
                        <SelectItem value="LOA">LOA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {vwfsType && (
                    <div className="space-y-2">
                      <Label className="flex items-center space-x-2">
                        <Building2Icon className="h-4 w-4" />
                        <span>SIRET VWFS (auto-déterminé)</span>
                      </Label>
                      <Input
                        value={getSiretForVwfsType(vwfsType)}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>{"Numéro d'affaire VWFS *"}</Label>
                    <Input
                      placeholder="Ex: 2025123456LLD0"
                      value={businessNumber}
                      onChange={(e) => setBusinessNumber(e.target.value)}
                    />
                    <Alert>
                      <InfoIcon className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        Format: année (4 chiffres) + 6 chiffres + suffixe LLD0,
                        LLD1, etc.
                        <br />
                        <strong>Importance métier:</strong>
                        {
                          "Ce numéro permet d'identifier de manière unique le contrat de financement VWFS et assure la traçabilité dans tous les systèmes Volkswagen."
                        }
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
              )}

              {/* b) Non-VWFS financing */}
              {fundingType === "other" && (
                <div className="space-y-4 rounded-lg border-2 p-4">
                  <Alert>
                    <InfoIcon className="h-4 w-4" />
                    <AlertDescription>
                      Le SIRET saisi ne doit pas correspondre à un SIRET VWFS.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <Label>Type de location *</Label>
                    <Select
                      value={otherLeasingType}
                      onValueChange={setOtherLeasingType}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner le type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LLD">
                          {"LLD (Location Longue Durée)"}
                        </SelectItem>
                        <SelectItem value="LOA">
                          {"LOA (Location avec Option d'Achat)"}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {nonVwfsLessorsQuery.isPending ? (
                    <Skeleton className="h-4 w-[200px] space-y-2" />
                  ) : (
                    <div className="space-y-2">
                      <Label>SIRET du loueur *</Label>
                      <Input
                        placeholder="Ex: 12345678901234"
                        value={lessorSiret}
                        onChange={(e) => {
                          setLessorSiret(e.target.value);
                          validateSiret(e.target.value);
                        }}
                        className={siretError ? "border-red-500" : ""}
                      />

                      {siretError && (
                        <Alert variant="destructive">
                          <AlertTriangleIcon className="h-4 w-4" />
                          <AlertDescription className="text-sm">
                            {siretError}
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Numéro de bon de commande loueur *</Label>
                    <Input
                      placeholder="Numéro BC loueur"
                      value={orderNumber}
                      onChange={(e) => setOrderNumber(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Required fields for all lease types */}
              {(fundingType === "vwfs" || fundingType === "other") && (
                <div className="space-y-4 rounded-lg p-4">
                  <div className="mb-2 flex items-center space-x-2">
                    <InfoIcon className="h-5 w-5 text-yellow-700" />
                    <Label className="text-base font-semibold text-yellow-900">
                      Données du contrat
                    </Label>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center space-x-2">
                        <CalendarIcon className="h-4 w-4" />
                        <span>Durée du contrat (mois) *</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="Ex: 36"
                        value={contractDuration}
                        onChange={(e) => setContractDuration(e.target.value)}
                        min="1"
                        max="120"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center space-x-2">
                        <GaugeIcon className="h-4 w-4" />
                        <span>Kilomètres maximum *</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="Ex: 45000"
                        value={maxKm}
                        onChange={(e) => setMaxKm(e.target.value)}
                        min="1000"
                        step="1000"
                      />
                    </div>
                  </div>

                  {(Number.parseInt(contractDuration) > 96 ||
                    Number.parseInt(maxKm) < 5000) && (
                    <Alert>
                      <AlertTriangleIcon className="h-4 w-4" />
                      <AlertDescription className="text-sm">
                        Attention: Valeurs inhabituelles détectées. Veuillez
                        vérifier les données saisies.
                      </AlertDescription>
                    </Alert>
                  )}

                  <Label className="flex items-center space-x-2">
                    <UserIcon className="h-4 w-4" />
                    <span>Informations conducteur (facultatives) </span>
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center space-x-2">
                        <span>Prénom</span>
                      </Label>
                      <Input
                        value={driverFirstName}
                        onChange={(e) => setDriverFirstName(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center space-x-2">
                        <span>Nom</span>
                      </Label>
                      <Input
                        value={driverLastName}
                        onChange={(e) => setDriverLastName(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Selection summary */}
          {validForm() && (
            <div className="rounded-lg bg-blue-50 p-4">
              <h4 className="mb-3 font-semibold text-gray-900">
                Résumé de la sélection
              </h4>
              <div className="flex flex-wrap gap-2">
                {isLeased === "yes" ? (
                  <>
                    <Badge className="bg-green-600 text-white">
                      Véhicule loué
                    </Badge>
                    {fundingType === "vwfs" && vwfsType && (
                      <Badge className="bg-[#005EA8] text-white">
                        VWFS - {vwfsType}
                      </Badge>
                    )}
                    {fundingType === "other" && otherLeasingType && (
                      <Badge className="bg-orange-600 text-white">
                        Autre loueur - {otherLeasingType}
                      </Badge>
                    )}
                    {contractDuration && (
                      <Badge variant="outline">{contractDuration} mois</Badge>
                    )}
                    {maxKm && (
                      <Badge variant="outline">
                        {Number.parseInt(maxKm).toLocaleString()} km
                      </Badge>
                    )}
                  </>
                ) : (
                  <>
                    <Badge className="">Véhicule non loué</Badge>
                    {fundingType === "credit" && (
                      <Badge variant="outline">Crédit classique</Badge>
                    )}
                    {fundingType === "cash" && (
                      <Badge variant="outline">Comptant</Badge>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
